import {
  isStockOptionFilterField,
  stockFilterOptionField,
} from "@/modules/inventory/domain/stock-filter-fields";
import { inventoryApi } from "@/modules/inventory/presentation/api/client";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDebounceCallback,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type SearchParams = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
  filters?: FilterConditionDTO[] | string;
};

function labelI18n(name?: unknown): string {
  if (!name || typeof name !== "object") return "—";
  const n = name as { lo?: string; th?: string; en?: string };
  return n.lo ?? n.th ?? n.en ?? "—";
}

function buildFilters(
  current: FilterConditionDTO[],
  updates: {
    search?: string;
    lowStock?: boolean;
  },
): FilterConditionDTO[] {
  let next = removeConditions(current, "search") ?? [];
  next = removeConditions(next, "lowStock") ?? [];
  if (updates.search) {
    next = upsertCondition(next, {
      field: "search",
      op: "contains",
      value: updates.search,
    });
  }
  if (updates.lowStock === true) {
    next = upsertCondition(next, {
      field: "lowStock",
      op: "eq",
      value: true,
    });
  }
  return next;
}

function stripProductAndOptionFilters(
  filters: FilterConditionDTO[],
): FilterConditionDTO[] {
  return filters.filter(
    (f) =>
      f.field !== "productId" &&
      !isStockOptionFilterField(f.field) &&
      f.field !== "optionValueId",
  );
}

export function StockFilter() {
  const nav = useNavigate({ from: "/app/inventory/stock" });
  const search = useSearch({ from: "/app/inventory/stock" }) as SearchParams;

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const searchFilter = findCondition(filters, "search");
  const lowStockFilter = findCondition(filters, "lowStock");
  const productIdFilter = findCondition(filters, "productId");

  const productIdValue = (productIdFilter?.value as string) ?? "";

  const filterProducts = useQuery({
    queryKey: ["inventory", "stock", "filter-products"],
    queryFn: () => inventoryApi.stock.listFilterProducts(),
  });

  const productOptions = useQuery({
    queryKey: ["inventory", "stock", "filter-product-options", productIdValue],
    queryFn: () => inventoryApi.stock.listFilterProductOptions(productIdValue),
    enabled: Boolean(productIdValue),
  });

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) ?? "",
  );
  const lowStockValue = lowStockFilter?.value === true;

  useEffect(() => {
    setSearchValue((searchFilter?.value as string) ?? "");
  }, [searchFilter?.value]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchValue(val);
    const next = buildFilters(filters, {
      search: val || undefined,
      lowStock: lowStockValue || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  }, 400);

  const applyLowStock = (val: boolean) => {
    const next = buildFilters(filters, {
      search: searchValue || undefined,
      lowStock: val || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const handleProductChange = (value: string) => {
    let next = stripProductAndOptionFilters(filters);
    if (value && value !== "all") {
      next = [...next, { field: "productId", op: "eq" as const, value }];
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const handleOptionValueChange = (optionId: string, value: string) => {
    const field = stockFilterOptionField(optionId);
    let next = removeConditions(filters, field) ?? [];
    if (value && value !== "all") {
      next = upsertCondition(next, {
        field,
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const hasStockOptFilter = filters.some(
    (f) =>
      isStockOptionFilterField(f.field) &&
      f.op === "eq" &&
      f.value,
  );

  const hasFilter = Boolean(
    searchFilter?.value ||
      lowStockFilter?.value ||
      productIdFilter?.value ||
      hasStockOptFilter ||
      findCondition(filters, "optionValueId")?.value,
  );

  const clearFilters = () => {
    setSearchValue("");
    nav({
      search: {
        ...search,
        offset: 0,
        filters: undefined,
      } as Record<string, unknown>,
    });
  };

  const productRows = filterProducts.data ?? [];
  const optionGroups = productOptions.data ?? [];

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-4 px-2"
      data-tourid="inventory-stock-filter"
    >
      <Input
        placeholder="ຄົ້ນຫາຕາມຊື່ ຫຼື SKU..."
        defaultValue={searchValue}
        onChange={(e) => {
          const v = e.target.value;
          debouncedSearch(v);
        }}
        className="h-8 sm:max-w-2xs"
      />
      <Select
        value={productIdValue || "all"}
        onValueChange={handleProductChange}
        disabled={filterProducts.isLoading}
      >
        <SelectTrigger className="h-8 w-[200px]">
          <SelectValue placeholder="ສິນຄ້າ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທັງໝົດ</SelectItem>
          {productRows.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {labelI18n(p.name)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {productIdValue
        ? optionGroups.map((opt) => {
            const field = stockFilterOptionField(opt.id);
            const cond = findCondition(filters, field);
            const selected = (cond?.value as string) ?? "";

            return (
              <div key={opt.id} className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs leading-none">
                  {labelI18n(opt.name)}
                </span>
                <Select
                  value={selected || "all"}
                  onValueChange={(v) => handleOptionValueChange(opt.id, v)}
                  disabled={productOptions.isLoading}
                >
                  <SelectTrigger className="h-8 min-w-[160px] max-w-[220px]">
                    <SelectValue placeholder={labelI18n(opt.name)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທັງໝົດ</SelectItem>
                    {opt.values.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {labelI18n(v.value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })
        : null}
      <label
        htmlFor="low-stock"
        className="flex cursor-pointer items-center gap-2 text-sm"
      >
        <Checkbox
          id="low-stock"
          checked={lowStockValue}
          onCheckedChange={(val) => applyLowStock(Boolean(val))}
        />
        ສະຕ໋ອກຕ່ຳ
      </label>
      {hasFilter && (
        <Button variant="ghost" size="sm" onClick={clearFilters}>
          <XIcon className="h-4 w-4" /> ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
